import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useSearch } from '../context/SearchContext';
import CartIcon from './CartIcon';
import WishlistIcon from './WishlistIcon';
import LanguageSelector from './LanguageSelector';
import ThemeToggle from './ThemeToggle';
import { useCategories } from '../context/CategoryContext';
import { useContent } from '../context/ContentContext';
import { categoryPath } from '../utils/categoryUrl';

/*
 * Modül seviyesinde tanımlı olmalı. Bileşenin içinde tanımlanırsa her render'da
 * yeni bir fonksiyon kimliği oluşur ve React alt ağacı söküp yeniden kurar.
 * Aktiflik durumu artık kapanış (closure) yerine prop olarak geliyor.
 */
const NavLink = ({ to, active, children }) => (
  <Link
    to={to}
    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
      active
        ? 'text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30'
        : 'text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100 hover:bg-stone-100 dark:hover:bg-stone-800'
    }`}
  >
    {children}
  </Link>
);

const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const { t } = useLanguage();
  const { updateFilters, searchProducts } = useSearch();
  const { categories: CATEGORIES } = useCategories();
  const { getSection } = useContent();

  // Mega menünün alt şeridindeki rozetler — backend erişilemezse bu liste devreye girer
  const navBadges = getSection('navBadge', [
    { titleKey: 'halalCertified',      icon: 'fa-certificate' },
    { titleKey: 'sustainableShipping', icon: 'fa-truck-fast' },
    { titleKey: 'allNaturalContent',   icon: 'fa-leaf' },
  ]);
  const [showCategories, setShowCategories] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileAccordion, setMobileAccordion] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleScroll  = () => setIsScrolled(window.scrollY > 40);
    const handleOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowCategories(false);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('mousedown', handleOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleOutside);
    };
  }, []);

  // Mobil menü açıkken body scroll'u kilitle
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const isActive = (path) => location.pathname === path;

  // Mega menü artık admin panelden yönetilen kategori sayısına göre şekilleniyor:
  // en fazla 5 sütun, sütun genişliği sabit (820px / 5) — az kategoride menü daralır.
  const MENU_COLUMN_WIDTH = 164;
  const menuColumns = Math.min(Math.max(CATEGORIES.length, 1), 5);
  const menuWidth = menuColumns * MENU_COLUMN_WIDTH;

  const handleLogout = () => {
    if (window.confirm(t('confirmLogout'))) logout();
  };

  const handleNavClick = (catId, subId = '') => {
    const filters = {
      category: catId,
      subcategory: subId,
      priceRange: { min: 0, max: 100 },
      rating: 0,
      inStock: false,
      isNew: false,
      isPopular: false,
      isDiscount: false,
      allergens: [],
    };
    updateFilters(filters);
    searchProducts('', filters);
    setShowCategories(false);
    setMobileMenuOpen(false);
    navigate(categoryPath(catId, subId));
  };

  return (
    <header
      className={`sticky top-0 z-nav transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 dark:bg-stone-950/95 backdrop-blur-xl shadow-nav border-b border-stone-100 dark:border-stone-800/60 py-3'
          : 'bg-white dark:bg-stone-950 py-4'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-4">

        {/* ── Logo ── */}
        <div className="flex items-center gap-8 shrink-0">
          <Link to="/" className="flex flex-col group">
            <span className="text-xl font-display font-bold text-stone-900 dark:text-stone-100 tracking-tight leading-none">
              E-TİCARET
            </span>
            <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-600 dark:text-amber-400 mt-0.5">
              ANADOLU İKRAMLARI
            </span>
          </Link>

          {/* ── Desktop Nav ── */}
          <nav className="hidden lg:flex items-center gap-1">
            <NavLink to="/" active={isActive('/')}>{t('home')}</NavLink>

            {/* Kategoriler Dropdown */}
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setShowCategories(!showCategories)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center gap-1.5 ${
                  location.pathname.startsWith('/products') || showCategories
                    ? 'text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30'
                    : 'text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100 hover:bg-stone-100 dark:hover:bg-stone-800'
                }`}
              >
                {t('collections')}
                <i className={`fas fa-chevron-down text-[10px] transition-transform duration-300 ${showCategories ? 'rotate-180' : ''}`} />
              </button>

              {/* Mega Menu */}
              <div
                style={{ width: `${menuWidth}px` }}
                className={`absolute top-full left-1/2 -translate-x-1/3 mt-3 bg-white dark:bg-stone-900 rounded-2xl shadow-modal border border-stone-100 dark:border-stone-800 overflow-hidden transition-all duration-300 origin-top z-overlay ${
                  showCategories
                    ? 'opacity-100 translate-y-0 scale-100 pointer-events-auto'
                    : 'opacity-0 -translate-y-2 scale-[0.98] pointer-events-none'
                }`}
              >
                <div className="h-1 w-full bg-gradient-to-r from-amber-400 via-amber-600 to-amber-800" />
                <div className="px-8 py-12">
                  <div className="flex items-center justify-between mb-6 pb-5 border-b border-stone-100 dark:border-stone-800">
                    <div>
                      <h3 className="text-lg font-semibold text-stone-900 dark:text-stone-100">
                        {t('collectionsMenuTitleLine1')}{' '}
                        <span className="text-amber-600">{t('collectionsMenuTitleHighlight')}</span>
                      </h3>
                      <p className="text-xs text-stone-500 mt-0.5">{t('collectionsMenuSubtitle')}</p>
                    </div>
                    <button
                      onClick={() => handleNavClick('')}
                      className="btn-primary text-xs px-4 py-2 rounded-lg"
                    >
                      {t('viewAllCollections')}
                    </button>
                  </div>

                  <div
                    className="grid gap-6"
                    style={{ gridTemplateColumns: `repeat(${menuColumns}, minmax(0, 1fr))` }}
                  >
                    {CATEGORIES.map((cat) => (
                      <div key={cat.id} className="space-y-3">
                        <button
                          onClick={() => handleNavClick(cat.id)}
                          className="flex items-center gap-2 group/cat w-full text-left"
                        >
                          <span className="text-xl transition-transform duration-200 group-hover/cat:scale-110">
                            {cat.icon}
                          </span>
                          <span className="text-sm font-semibold text-stone-900 dark:text-stone-100 group-hover/cat:text-amber-600 transition-colors">
                            {cat.label}
                          </span>
                        </button>
                        <div className="flex flex-col gap-1.5">
                          {(cat.subcategories || []).map((sub) => (
                            <button
                              key={sub.id}
                              onClick={() => handleNavClick(cat.id, sub.id)}
                              className="text-xs text-stone-500 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100 hover:translate-x-0.5 transition-all text-left flex items-center gap-1.5"
                            >
                              <span className="w-1 h-1 rounded-full bg-stone-300 dark:bg-stone-700 shrink-0" />
                              {sub.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-stone-50 dark:bg-stone-800/50 border-t border-stone-100 dark:border-stone-800 px-8 py-5 flex items-center justify-center gap-10">
                  {navBadges.map((item, index) => (
                    <React.Fragment key={item.titleKey}>
                      <div className="flex items-center gap-2">
                        <i className={`fas ${item.icon} text-amber-600 text-sm`} />
                        <span className="text-xs font-medium text-stone-600 dark:text-stone-300">
                          {t(item.titleKey)}
                        </span>
                      </div>
                      {index < navBadges.length - 1 && (
                        <div className="w-px h-4 bg-stone-200 dark:bg-stone-700" />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>

            <NavLink to="/about" active={isActive('/about')}>{t('ourStory')}</NavLink>
          </nav>
        </div>

        {/* ── Sağ Araçlar ── */}
        <div className="flex items-center gap-1 sm:gap-2">
          <div className="hidden sm:block"><LanguageSelector /></div>
          <div className="hidden sm:block"><ThemeToggle /></div>
          <div className="hidden sm:block w-px h-5 bg-stone-200 dark:bg-stone-700 mx-1" />

          <WishlistIcon />
          <CartIcon />

          <div className="w-px h-5 bg-stone-200 dark:bg-stone-700 mx-1" />

          {isAuthenticated ? (
            <>
              {user?.role === 'admin' && (
                <Link
                  to="/admin/dashboard"
                  className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 hover:bg-amber-100 dark:hover:bg-amber-950/50 transition-colors"
                >
                  <i className="fas fa-gauge text-xs" />
                  <span className="hidden md:block">Admin Paneli</span>
                </Link>
              )}
              <Link
                to="/profile"
                className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700 transition-colors"
              >
                <div className="w-7 h-7 rounded-lg bg-amber-600 text-white flex items-center justify-center text-xs font-bold shrink-0">
                  {user?.firstName?.charAt(0)?.toUpperCase()}
                </div>
                <span className="hidden md:block text-sm font-medium text-stone-700 dark:text-stone-300">
                  {user?.firstName}
                </span>
              </Link>
            </>
          ) : (
            <Link
              to="/login"
              className="hidden sm:inline-flex btn-primary text-sm px-4 py-2 rounded-lg items-center"
            >
              {t('login')}
            </Link>
          )}

          {/* Mobil hamburger */}
          <button
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Menüyü aç"
            className="lg:hidden w-9 h-9 rounded-lg flex items-center justify-center text-stone-700 dark:text-stone-300 bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700 transition-colors"
          >
            <i className="fas fa-bars text-sm" />
          </button>
        </div>
      </div>

      {/* ── Mobil Menü Overlay ── */}
      <div
        className={`fixed inset-0 z-overlay bg-black/40 backdrop-blur-sm transition-opacity duration-300 lg:hidden ${
          mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setMobileMenuOpen(false)}
      />

      {/* ── Mobil Menü Drawer ── */}
      <div
        className={`fixed top-0 right-0 bottom-0 z-modal w-80 max-w-full bg-white dark:bg-stone-950 shadow-modal transition-transform duration-300 ease-out lg:hidden flex flex-col ${
          mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-stone-100 dark:border-stone-800">
          <span className="text-lg font-display font-bold text-stone-900 dark:text-stone-100">Menü</span>
          <button
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Menüyü kapat"
            className="w-8 h-8 rounded-lg flex items-center justify-center text-stone-500 hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors"
          >
            <i className="fas fa-times" />
          </button>
        </div>

        {/* Links */}
        <nav className="flex-1 overflow-y-auto p-5 space-y-1">
          <Link
            to="/"
            onClick={() => setMobileMenuOpen(false)}
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium text-stone-800 dark:text-stone-100 hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors"
          >
            <i className="fas fa-home w-4 text-stone-400" />
            {t('home')}
          </Link>

          {/* Kategoriler accordion */}
          <div>
            <button
              onClick={() => setMobileAccordion(mobileAccordion === '__cat' ? null : '__cat')}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl text-base font-medium text-stone-800 dark:text-stone-100 hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors"
            >
              <span className="flex items-center gap-3">
                <i className="fas fa-grid-2 w-4 text-stone-400" />
                {t('collections')}
              </span>
              <i className={`fas fa-chevron-right text-xs text-stone-400 transition-transform duration-200 ${mobileAccordion === '__cat' ? 'rotate-90' : ''}`} />
            </button>

            {mobileAccordion === '__cat' && (
              <div className="mt-1 ml-4 pl-4 border-l-2 border-amber-100 dark:border-amber-900/30 space-y-1">
                {CATEGORIES.map((cat) => (
                  <div key={cat.id}>
                    <button
                      onClick={() => setMobileAccordion(mobileAccordion === cat.id ? '__cat' : cat.id)}
                      className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium text-stone-700 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors"
                    >
                      <span className="flex items-center gap-2">
                        <span>{cat.icon}</span>
                        {cat.label}
                      </span>
                      <i className={`fas fa-chevron-right text-[10px] text-stone-400 transition-transform ${mobileAccordion === cat.id ? 'rotate-90' : ''}`} />
                    </button>
                    {mobileAccordion === cat.id && (
                      <div className="ml-4 pl-3 border-l border-stone-100 dark:border-stone-800 mt-1 space-y-1">
                        {(cat.subcategories || []).map((sub) => (
                          <button
                            key={sub.id}
                            onClick={() => handleNavClick(cat.id, sub.id)}
                            className="w-full text-left px-3 py-1.5 text-sm text-stone-500 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100 transition-colors"
                          >
                            {sub.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/campaigns"
            onClick={() => setMobileMenuOpen(false)}
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium text-stone-800 dark:text-stone-100 hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors"
          >
            <i className="fas fa-gift w-4 text-stone-400" />
            {t('campaignsTitle')}
          </Link>
          <Link
            to="/about"
            onClick={() => setMobileMenuOpen(false)}
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium text-stone-800 dark:text-stone-100 hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors"
          >
            <i className="fas fa-book-open w-4 text-stone-400" />
            {t('ourStory')}
          </Link>
          <Link
            to="/support"
            onClick={() => setMobileMenuOpen(false)}
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium text-stone-800 dark:text-stone-100 hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors"
          >
            <i className="fas fa-headset w-4 text-stone-400" />
            {t('support')}
          </Link>
        </nav>

        {/* Footer */}
        <div className="p-5 border-t border-stone-100 dark:border-stone-800 space-y-4">
          {isAuthenticated && user?.role === 'admin' && (
            <Link
              to="/admin/dashboard"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center justify-center gap-2 w-full text-center rounded-xl py-3 text-sm font-semibold text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 hover:bg-amber-100 dark:hover:bg-amber-950/50 transition-colors"
            >
              <i className="fas fa-gauge" />
              Admin Paneli
            </Link>
          )}
          {!isAuthenticated && (
            <Link
              to="/login"
              onClick={() => setMobileMenuOpen(false)}
              className="btn-primary w-full text-center rounded-xl py-3 text-sm font-semibold"
            >
              {t('login')}
            </Link>
          )}
          <div className="flex items-center justify-between">
            <div className="flex gap-3">
              {[
                { href: '#', icon: 'fab fa-instagram' },
                { href: '#', icon: 'fab fa-facebook-f' },
                { href: '#', icon: 'fab fa-whatsapp' },
              ].map((s) => (
                <a
                  key={s.icon}
                  href={s.href}
                  className="w-9 h-9 rounded-lg bg-stone-100 dark:bg-stone-800 flex items-center justify-center text-stone-500 hover:text-amber-600 transition-colors"
                >
                  <i className={s.icon} />
                </a>
              ))}
            </div>
            <div className="flex gap-2">
              <LanguageSelector />
              <ThemeToggle />
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navigation;
